import { of as observableOf, Observable, fromEvent } from 'rxjs';
import { switchMap, shareReplay, map } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { NamedEntity } from './../models/named-entity.model';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { User } from '../models/identity/user.model';
import { Grade, UserPersonInfo } from '../models/identity';
import { EmployeeSearchRequest } from '../common/employee-selection/employee-search-request.model';
import { EmployeeAdvancedSearchRequest } from '../common/employee-selection/employee-advanced-search-request.model';

@Injectable()
export class UserService {

    private readonly apiController = 'api/User';
    private readonly employee: User;

    get getUser(): User {
        return this.employee;
    }

    constructor(private readonly httpService: HttpClient, private configService: ConfigService) {
    }

    getCurrentUser(): Observable<User> {
        return this.httpService.get(`${this.apiController}/getcurrentuser`).pipe(shareReplay()) as Observable<User>;
    }

    getEmployee(id: number): Observable<NamedEntity> {
        return this.httpService.get<NamedEntity>(`${this.apiController}/employee/${id}`);
    }

    getTeam(): Observable<Array<NamedEntity>> {
        return this.httpService.get(`${this.apiController}/getteam`).pipe(shareReplay()) as Observable<Array<NamedEntity>>;
    }

    getRespondentByFilter(searchText: string, businessUnitId: number = null): Observable<Array<NamedEntity>> {
        return this.httpService.get(`${this.apiController}/${searchText}/${businessUnitId}`) as Observable<Array<NamedEntity>>;
    }

    getByAdvancedSearch(request: EmployeeAdvancedSearchRequest) {
        return this.httpService.put(`${this.apiController}/advancedSearch`, request) as Observable<Array<NamedEntity>>;
    }

    getByBusinessUnitId(businessUnitId: number) {
        return this.httpService.get(`${this.apiController}/${businessUnitId}`) as Observable<Array<NamedEntity>>;
    }

    getBySearchPattern(searchText: string, isLeaversSearched: boolean = false) {
        return this.httpService.get(`${this.apiController}/search/${searchText}/${isLeaversSearched}`) as Observable<Array<UserPersonInfo>>;
    }

    getGrades(request?: EmployeeSearchRequest) {
        request = request ? request : {};
        return this.httpService.put(`${this.apiController}/grades`, request) as Observable<Array<Grade>>;
    }

    getPositions(request: EmployeeSearchRequest) {
        return this.httpService.put(`${this.apiController}/positions/`, request) as Observable<Array<NamedEntity>>;
    }

    getEmployees(request: EmployeeSearchRequest) {
        return this.httpService.put(`${this.apiController}/search`, request) as Observable<Array<NamedEntity>>;
    }

    getApprovalEmployees(searchText: string, params?: HttpParams) {
        return this.httpService.get(`${this.apiController}/searchForApproval/${searchText}`,
            {params: params}) as Observable<Array<NamedEntity>>;
    }

    getAvatar(employeeId: number): Observable<string | ArrayBuffer> {
        return this.httpService
         .get(`${this.apiController}/picture/${employeeId}`, getImageRequestOptions())
         .pipe(
           switchMap(x => createImageFromBlob(x as Blob))
         )
     }
}

export function getImageRequestOptions(): Object {
    const options = {
      headers: new HttpHeaders(
        {
          'Accept': 'image/jpeg',
          'Content-Type': 'image/jpeg'
        }),
      responseType: 'blob'
    };
    return options;
  }

export const createImageFromBlob = (imageBlob: Blob): Observable<string | ArrayBuffer> => {
    const reader = new FileReader();
  
    if (imageBlob) {
      reader.readAsDataURL(imageBlob);
    }
  
    return fromEvent(reader, 'load').pipe(map(() => {
      const result = reader.result.toString().replace("data:", "");
      return result.length > 0 ? reader.result : undefined;
    }));
  }